import { Component, ViewChild, effect, signal, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IEntityMetaData } from '@app/common-codes/_models/IEntityMetaData';
import { GenericListComponent } from '@app/common-codes/components/list-components/generic-base-list/generic-list.component';
import { Register } from '@app/common-codes/decorators/DependencyInjectionRegistreyDecorators';
import { ListTableColumnComponent } from '@app/common-codes/components/list-components/list-table-column/list-table-column.component';
import { ListViewComponent } from '@app/common-codes/components/list-components/list-view/list-view.component';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { AlertModalService } from '@app/common-codes/services/AlertModalService';
import { AlertModalResult } from '@app/common-codes/components/nested-components/alert-dialog/alert-dialog.component';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { GenetricDetailComponent } from '@app/common-codes/components/edit-components/generic-detail.component';
import { Visit } from '@app/common-codes/models/matwins-tenant/Visit.model';
import { LookupProfile, LookupItem } from '@app/common-codes/services/LookupService';
import { VisitDetailComponent } from '../visit-detail/visit-detail.component';
import { ReactiveBadgeComponent } from '@app/common-codes/components/nested-components/reactive-badge/reactive-badge.component';
import { Feedback } from '@app/common-codes/models/matwins-tenant/Feedback.model';
import { BubbleSmileyComponent } from '@app/common-codes/components/nested-components/bubble-smiley/bubble-smiley.component';
import { FSTREAM_Image } from '@app/common-codes/models/matwins-tenant/FSTREAM_Image.model';
import { MultiCriteriaFilterComponent } from '@app/common-codes/components/nested-components/multi-criteria-filter/multi-criteria-filter.component';
import { TimeZoneIconComponent } from '@app/common-codes/components/nested-components/timezone-icon/timezone-icon.component';
import { FilterOption } from '@app/common-codes/components/nested-components/multi-criteria-filter/multi-criteria-filter.component';
import { IFilterOperator, IFilterCriteria } from '@app/common-codes/components/_primitives/IFilterCriteria';
import { IRefreshDateRangeValue } from '@app/common-codes/components/_primitives/INeedRefreshData';
import { StructuredDataRequest } from '@app/common-codes/_DTOs/IStructuredData';
import { ToInstantString } from '@app/common-codes/helpers/dateHelpers';
import { ToastType } from '@app/common-codes/services/AlertModalService';
import { CurrentTenantService } from '@app/common-codes/services/CurrentTenantService';
import { filter } from 'rxjs';

@Register(Visit)
@Component({
  selector: 'app-visit-list',
  templateUrl: './visit-list.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    ListViewComponent,
    VisitDetailComponent,
    ListTableColumnComponent,
    TranslatePipe,
    ReactiveBadgeComponent,
    BubbleSmileyComponent,
    MultiCriteriaFilterComponent,
    TimeZoneIconComponent
  ]
})
export class VisitListComponent extends GenericListComponent<Visit> {

  override Skin = MatwinsSkins.V1 as IStaticStylesConfig;
  override ComponentEntityClass = Visit;
  override entityMetaData: IEntityMetaData = new Visit().GetType();
  override contextMenuItems = [];
  modalEntity = signal<Visit | null>(null);
  @ViewChild('visitDetail', { read: VisitDetailComponent }) visitDetail!: VisitDetailComponent;

  visitStatusLookup = signal<LookupItem[]>([]);
  visitorTypesLookup = signal<LookupItem[]>([]);
  filterOptions = signal<FilterOption[]>([]);           // filtres multicritères

  // Bouton "Alerter" : visible quand le site a active l'alerte d'evacuation.
  // Le flag vient du POS affiche (fkPointOfServiceId) via le tenant courant,
  // avec repli sur le POS courant (contexte iPad).
  private currentTenantService = inject(CurrentTenantService);
  get fireAlertEnabled(): boolean {
    const pos = this.currentTenantService.currentTenant()?.pointOfServices
      ?.find(p => p.id === this.fkPointOfServiceId)
      ?? this.currentTenantService.currentPointOfService();
    return !!pos?.dS_FireAlert;
  }

  suggestionLookupArray: LookupItem[] = [
    { id: '1', key: '1', value: 'Suggest', visible: true, additionalData: { containerdisplaystate: 'containerBadge rounded warning', displaystate: 'badge' } }
  ];

  public static override get lookupProfiles(): LookupProfile[] {
    return [
      { lookupName: "VisitStatus" },
      { lookupName: "PointOfService" },
      { lookupName: "TimeZone" },
      { lookupName: "VisitorType" },
      { lookupName: "Employee" },
      { lookupName: "AccessCard" }
    ];
  }

  tooltipVisibility = new Map<number, boolean>();

  constructor(_alertModalService: AlertModalService) {
    super();
    this.alertModalService = _alertModalService;




    effect(() => {
      const entities = this.listView()?.entities();
      if (entities) {
        // Lire la revision : si les listes arrivent en retard, l'effet repasse et les remplit.
        this.lookupService.revision();
        this.visitStatusLookup.set(this.lookupService.getLookupData('VisitStatus'));
        this.visitorTypesLookup.set(this.lookupService.getLookupData('VisitorType'));
      }
    });


    const filterColors: string[] = ['red', 'sky', 'green', 'orange', 'yellow', 'blue', 'purple'];

    effect(() => {
      let colorIndex = 0;
      // Création des options de filtre à partir des deux lookups
      const selectedIds = new Set(this.selectedFilterOptionIds());
      const statusOptions: FilterOption[] = this.visitStatusLookup()
        .filter((item) => { return item.value.toLowerCase() !== 'unknown' })
        .map((item: LookupItem) => ({
          id: `visitStatus_${item.key}`,
          label: item.value,
          color: filterColors[colorIndex++],
          isSelected: selectedIds.has(`visitStatus_${item.key}`),
          group: 'VisitStatus',
          fieldName: 'status'
        }));
      const visitorTypeOptions: FilterOption[] = this.visitorTypesLookup()
        .filter((item) => { return item.value.toLowerCase() !== 'unknown' })
        .map((item: LookupItem) => ({
          id: `visitorType_${item.key}`,
          label: item.value,
          color: filterColors[colorIndex++],
          isSelected: selectedIds.has(`visitorType_${item.key}`),
          group: 'VisitorType',
          fieldName: 'visitorType'
        }));
      // Union des deux tableaux
      this.filterOptions.set([...statusOptions, ...visitorTypeOptions]);
    });
  }

  onSearchInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
    this.listView()?.onSearch(value);
  }

  override ngOnInit() {
    super.ngOnInit();
  }

  override ngAfterViewInit() {
    super.ngAfterViewInit();
  }

  override getdetailComponent(): GenetricDetailComponent<Visit> | undefined {
    return this.visitDetail;
  }

  // Action pour la colonne "Suggestion"
  doSuggestionAction(entity: Visit, value: any) {
    alert(`Suggestion pour la visite ${entity.visitId} : ${value}`);
  }

  // Requete du tableau telle qu'affichee : le serveur la rejoue (recherche,
  // filtres, plage de dates, tri) pour l'export comme pour la sortie en masse.
  private buildListRequest(): StructuredDataRequest {
    const listView = this.listView();
    const refreshAllData = this.mediator?.needRefreshData();

    return {
      pageNumber: 1,
      pageSize: this.cacheSize,
      search: (listView?.searchTerm() ?? '').toLowerCase(),
      sortBy: listView?.sortBy() ?? '',
      sortOrder: listView?.sortOrder() ?? 'asc',
      searchColumns: listView?.getPagination().searchColumns?.join(',') ?? null,
      requiredLookups: [],
      dateRange: refreshAllData ? [{
        startDate: ToInstantString((refreshAllData.value as IRefreshDateRangeValue).startDate),
        endDate: ToInstantString((refreshAllData.value as IRefreshDateRangeValue).endDate),
        entityName: refreshAllData.entityName,
        columnName: refreshAllData.columnName
      }] : [],
      filters: this.buildServerFilters(),
      featureContext: this.featureContext,
      includedEntities: this.includedEntities
    };
  }

  // Export de la liste affichee : le serveur rejoue la requete du tableau sans
  // pagination. withDocuments => zip Excel + pieces.
  isExporting = signal(false);
  exportMenuOpen = signal(false);

  async exportVisits(withDocuments: boolean) {
    this.exportMenuOpen.set(false);
    if (this.isExporting()) return;

    this.isExporting.set(true);
    try {
      const params = this.buildListRequest();

      const blob = await this.dataService.download<Blob>(
        `export?withDocuments=${withDocuments}&lang=${this.translationService.currentLanguage}`, params);

      if (!blob) throw new Error('Réponse vide du serveur');

      const stamp = new Date().toISOString().slice(0, 16).replace(/\D/g, '');
      this.saveBlob(blob, `visites_${stamp}.${withDocuments ? 'zip' : 'xlsx'}`);
    } catch (error) {
      console.error('Erreur lors de l\'export des visites:', error);
      this.alertModalService?.showToast(this.translationService.instant('messages.error'), ToastType.Error, 5000);
    } finally {
      this.isExporting.set(false);
    }
  }

  // "Date de sortie" : passe a "Out" toutes les visites "In" du resultat affiche.
  // Cote serveur, le changement de statut declenche les handlers metier :
  // liberation des cartes d'acces et envoi des notifications de sortie.
  isCheckingOut = signal(false);

  async checkoutAllVisits() {
    if (this.isCheckingOut()) return;

    const confirm = await this.alertModalService?.showAlert({
      title: this.translationService.instant('entities.visit.propreties.exitDate'),
      message: this.translationService.instant('vues.visit.list.checkout-all-confirm'),
      yesButtonText: this.translationService.instant('buttons.yes'),
      noButtonText: this.translationService.instant('buttons.no'),
      Skin: this.Skin
    });
    if (confirm !== AlertModalResult.Yes) return;

    this.isCheckingOut.set(true);
    try {
      const result = await this.dataService.execute<{ updated: number }>('POST', 'checkoutAll', this.buildListRequest());
      this.alertModalService?.showToast(
        this.translationService.instant('vues.visit.list.checkout-all-done', { count: `${result?.updated ?? 0}` }),
        ToastType.Success, 5000);
      this.refreshList();
    } catch (error) {
      console.error('Erreur lors de la sortie en masse des visites:', error);
      this.alertModalService?.showToast(this.translationService.instant('messages.error'), ToastType.Error, 5000);
    } finally {
      this.isCheckingOut.set(false);
    }
  }

  // Alerte d'evacuation : notifie les visiteurs encore "In" du site
  // (email/SMS), leurs hotes (email/SMS/Teams) et les administrateurs
  // (courriel avec la liste des visiteurs en PDF).
  isAlerting = signal(false);

  async sendFireAlert() {
    if (this.isAlerting()) return;
    if (this.fkPointOfServiceId == null) return;

    const confirm = await this.alertModalService?.showAlert({
      title: this.translationService.instant('entities.visit.propreties.alert'),
      message: this.translationService.instant('vues.visit.list.alert-confirm'),
      yesButtonText: this.translationService.instant('buttons.yes'),
      noButtonText: this.translationService.instant('buttons.no'),
      Skin: this.Skin
    });
    if (confirm !== AlertModalResult.Yes) return;

    this.isAlerting.set(true);
    try {
      // Vue admin : on envoie la requete du tableau — le serveur la rejoue
      // pour prendre les visites de la vue active.
      await this.dataService.execute('POST', 'firealert', { ...this.buildListRequest(), posId: this.fkPointOfServiceId });
      this.alertModalService?.showToast(
        this.translationService.instant('vues.visit.list.alert-done'),
        ToastType.Success, 5000);
    } catch (error) {
      console.error('Erreur lors du declenchement de l\'alerte d\'evacuation:', error);
      this.alertModalService?.showToast(this.translationService.instant('messages.error'), ToastType.Error, 5000);
    } finally {
      this.isAlerting.set(false);
    }
  }

  private saveBlob(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  public getFeedBack(visit: Visit): Feedback {


    if (!visit.feedbacks || visit.feedbacks.length === 0) {
      visit.feedbacks = [new Feedback()];
      visit.feedbacks[0].fK_Visit = visit.id;
      visit.feedbacks[0].fK_PointOfService = visit.fK_PointOfService;
    }

    return visit.feedbacks[0];
  }


  downloadAgreements(visit: Visit) {
    // TODO: Implement download logic
    console.log('Downloading agreements for visit:', visit);

    //download du pdf generer à partir des champs du visiteur + pdf du rapport + rdlc du rapport
    // Appeler l'API pour générer le rapport complet
    this.dataService.download<Blob>('generateReport', visit)
      .then(reportBytes => {
        if (reportBytes) {
          // Convertir les bytes en blob et télécharger
          const blob = new Blob([reportBytes], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `rapport_visite_${visit.visitId}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          // this.alertModalService.showSuccess('Rapport généré et téléchargé avec succès');
        }
      })
      .catch(error => {
        console.error('Erreur lors de la génération du rapport:', error);
        // this.alertModalService.showError('Échec de la génération du rapport. Veuillez réessayer.');
      });
  }

  downloadEngagements(visit: Visit): void {
    this.downloadDocument(visit.imageEngagement, `engagement_${visit.visitId}.pdf`, 'l\'engagement');
  }

  downloadhealthy(visit: Visit): void {
    this.downloadDocument(visit.imageCovid, `healthy_${visit.visitId}.pdf`, 'le document santé');
  }

  downloadQuiz(visit: Visit): void {
    this.downloadDocument(visit.imageQuiz, `quiz_${visit.visitId}.pdf`, 'le quiz');
  }

  downloadOldAgreements(visit: Visit): void {
    this.downloadDocument(visit.imageAgreements, `agreement_${visit.visitId}.pdf`, 'l\'accord');
  }


  // À ajouter dans la classe
  private downloadDocument(
    image: FSTREAM_Image | undefined,
    defaultFileName: string,
    logLabel: string
  ): void {
    if (!image?.rawData_Path) return;

    try {
      const link = document.createElement('a');
      link.href = `${this.rootUrl}${image.rawData_Path}`;
      link.download = image.fileName || defaultFileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      console.log(`Téléchargement de ${logLabel} initié:`, image.fileName);
    } catch (error) {
      console.error(`Erreur lors du téléchargement de ${logLabel}:`, error);
    }
  }

  showTooltip(entity: Visit, show: boolean) {
    this.tooltipVisibility.set(entity.id, show);
  }

  isTooltipVisible(entity: Visit): boolean {
    return this.tooltipVisibility.get(entity.id) ?? false;
  }

  getCardIds(entity: Visit): string[] {
    if (!entity.attachedCards || entity.attachedCards.trim() === '') {
      return [];
    }
    return entity.attachedCards.split(',').map(s => s.trim()).filter(s => s !== '');
  }

  getCardCount(entity: Visit): number {
    return this.getCardIds(entity).length;
  }

  getFirstCardId(entity: Visit): string {
    const ids = this.getCardIds(entity);
    return ids.length > 0 ? ids[0] : '';
  }

  hasMultipleCards(entity: Visit): boolean {
    return this.getCardCount(entity) > 1;
  }
}
